import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Button, Space, Table, message, Row, Col, 
  InputNumber, Input, Select, Modal, Form, Tag, Alert, 
  Radio, Tooltip, Progress
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, WalletOutlined, SendOutlined,
  ReloadOutlined, DeleteOutlined, PlusOutlined, FilterOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import { saveAs } from 'file-saver';

// 接口定义
interface TransferPair {
  id: string;
  privateKey: string;
  senderAddress: string;
  receiverAddress: string;
  nativeBalance?: string;
  tokenBalance?: string;
  status: 'idle' | 'in-progress' | 'success' | 'error';
  statusMessage?: string;
  isSelected: boolean;
}

interface NetworkConfig {
  name: string;
  rpcUrl: string;
  chainId: number;
  symbol: string;
}

interface TokenInfo {
  address: string;
  symbol: string;
  decimals: number;
  name: string;
}

interface ConfigState {
  network: NetworkConfig;
  selectedToken: TokenInfo;
  amountMode: 'all' | 'fixed' | 'percentage';
  amountSettings: {
    fixed: string;
    percentage: number;
  };
  gasLimit: string;
  gasPrice: string;
  delayMin: number;
  delayMax: number;
  is1559: boolean;
}

// 预设网络配置
const NETWORK_CONFIGS: NetworkConfig[] = [
  {
    name: 'BNB Smart Chain Mainnet',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB'
  },
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH'
  },
];

// 原生代币配置
const NATIVE_TOKEN: TokenInfo = {
  address: 'native',
  symbol: 'ETH',
  decimals: 18,
  name: 'Native Token'
};

// ERC20 ABI
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)"
];

const MultiTransfer: React.FC = () => {
  // 状态管理
  const [transferPairs, setTransferPairs] = useState<TransferPair[]>([]);
  const [configState, setConfigState] = useState<ConfigState>({
    network: NETWORK_CONFIGS[0],
    selectedToken: { ...NATIVE_TOKEN, symbol: NETWORK_CONFIGS[0].symbol },
    amountMode: 'all',
    amountSettings: {
      fixed: '',
      percentage: 100
    },
    gasLimit: '',
    gasPrice: '',
    delayMin: 1,
    delayMax: 3,
    is1559: true
  });
  const [customTokens, setCustomTokens] = useState<TokenInfo[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [isTransferring, setIsTransferring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [liveGasPrice, setLiveGasPrice] = useState<string>('');
  
  // Modal 状态
  const [isSenderModalVisible, setIsSenderModalVisible] = useState(false);
  const [isReceiverModalVisible, setIsReceiverModalVisible] = useState(false);
  const [isTokenModalVisible, setIsTokenModalVisible] = useState(false);
  const [sendersInput, setSendersInput] = useState('');
  const [receiversInput, setReceiversInput] = useState('');
  const [tokenAddressInput, setTokenAddressInput] = useState('');

  const logsRef = useRef<HTMLDivElement>(null);

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    setLogs(prev => [...prev, logMessage]);
    
    // 自动滚动到底部
    setTimeout(() => {
      if (logsRef.current) {
        logsRef.current.scrollTop = logsRef.current.scrollHeight;
      }
    }, 100);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 更新转账对状态
  const updatePairStatus = (id: string, status: TransferPair['status'], statusMessage?: string) => {
    setTransferPairs(prev => prev.map(pair => 
      pair.id === id ? { ...pair, status, statusMessage } : pair
    ));
  };

  // 获取实时Gas价格
  const fetchLiveGasPrice = async () => {
    try {
      const provider = new ethers.JsonRpcProvider(configState.network.rpcUrl);
      const feeData = await provider.getFeeData();
      if (feeData.gasPrice) {
        const gasPriceGwei = ethers.formatUnits(feeData.gasPrice, 'gwei');
        setLiveGasPrice(parseFloat(gasPriceGwei).toFixed(2));
      }
    } catch (error) {
      console.error('获取Gas价格失败:', error);
    }
  };

  // 监听网络变化，更新Gas价格
  useEffect(() => {
    fetchLiveGasPrice();
    const interval = setInterval(fetchLiveGasPrice, 15000); // 每15秒更新一次
    return () => clearInterval(interval);
  }, [configState.network.rpcUrl]);

  // 监听网络变化，更新原生代币符号
  useEffect(() => {
    setConfigState(prev => ({
      ...prev,
      selectedToken: prev.selectedToken.address === 'native' 
        ? { ...prev.selectedToken, symbol: prev.network.symbol }
        : prev.selectedToken
    }));
  }, [configState.network]);

  // 导入发送地址
  const handleImportSenders = () => {
    if (!sendersInput.trim()) {
      message.error('请输入私钥');
      return;
    }

    const privateKeys = sendersInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const newPairs: TransferPair[] = [];
    const invalidKeys: string[] = [];

    privateKeys.forEach((privateKey, index) => {
      try {
        const wallet = new ethers.Wallet(privateKey);
        newPairs.push({
          id: `pair_${Date.now()}_${index}`,
          privateKey: privateKey,
          senderAddress: wallet.address,
          receiverAddress: '',
          status: 'idle',
          isSelected: false
        });
      } catch (error) {
        invalidKeys.push(`第${index + 1}行`);
      }
    });

    if (invalidKeys.length > 0) {
      message.error(`发现${invalidKeys.length}个无效私钥: ${invalidKeys.join(', ')}`);
      return;
    }

    if (newPairs.length === 0) {
      message.error('没有有效的私钥');
      return;
    }

    setTransferPairs(prev => [...prev, ...newPairs]);
    setIsSenderModalVisible(false);
    setSendersInput('');
    addLog(`成功导入${newPairs.length}个发送地址`, 'success');
    message.success(`成功导入${newPairs.length}个发送地址`);
  };

  // 导入接收地址
  const handleImportReceivers = () => {
    if (!receiversInput.trim()) {
      message.error('请输入接收地址');
      return;
    }

    const addresses = receiversInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // 验证地址格式
    const invalidAddresses = addresses.filter(addr => !isValidAddress(addr));
    if (invalidAddresses.length > 0) {
      message.error(`发现${invalidAddresses.length}个无效地址格式`);
      return;
    }

    // 找到接收地址为空的转账对
    const emptyReceiverPairs = transferPairs.filter(pair => !pair.receiverAddress);
    if (emptyReceiverPairs.length === 0) {
      message.warning('没有需要填充接收地址的转账对');
      return;
    }

    const updatedPairs = [...transferPairs];
    let filledCount = 0;

    for (let i = 0; i < addresses.length && filledCount < emptyReceiverPairs.length; i++) {
      const pairIndex = updatedPairs.findIndex(pair =>
        pair.id === emptyReceiverPairs[filledCount].id
      );
      if (pairIndex !== -1) {
        updatedPairs[pairIndex].receiverAddress = addresses[i];
        filledCount++;
      }
    }

    setTransferPairs(updatedPairs);
    setIsReceiverModalVisible(false);
    setReceiversInput('');
    addLog(`成功填充${filledCount}个接收地址`, 'success');
    message.success(`成功填充${filledCount}个接收地址`);
  };

  // 添加自定义代币
  const handleAddToken = async () => {
    if (!tokenAddressInput.trim() || !isValidAddress(tokenAddressInput)) {
      message.error('请输入有效的代币合约地址');
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(configState.network.rpcUrl);
      const tokenContract = new ethers.Contract(tokenAddressInput, ERC20_ABI, provider);

      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);

      const newToken: TokenInfo = {
        address: tokenAddressInput,
        name,
        symbol,
        decimals: Number(decimals)
      };

      // 检查是否已存在
      if (customTokens.some(token => token.address.toLowerCase() === tokenAddressInput.toLowerCase())) {
        message.warning('该代币已存在');
        return;
      }

      setCustomTokens(prev => [...prev, newToken]);
      setIsTokenModalVisible(false);
      setTokenAddressInput('');
      addLog(`成功添加代币: ${symbol} (${name})`, 'success');
      message.success(`成功添加代币: ${symbol}`);
    } catch (error) {
      console.error('添加代币失败:', error);
      addLog('添加代币失败，请检查合约地址', 'error');
      message.error('添加代币失败，请检查合约地址');
    }
  };

  // 筛选功能
  const handleFilterByBalance = (type: 'zero' | 'positive' | 'invert') => {
    const updatedPairs = transferPairs.map(pair => {
      const balance = configState.selectedToken.address === 'native'
        ? parseFloat(pair.nativeBalance || '0')
        : parseFloat(pair.tokenBalance || '0');

      let shouldSelect = false;
      switch (type) {
        case 'zero':
          shouldSelect = balance === 0;
          break;
        case 'positive':
          shouldSelect = balance > 0;
          break;
        case 'invert':
          shouldSelect = !pair.isSelected;
          break;
      }

      return { ...pair, isSelected: shouldSelect };
    });

    setTransferPairs(updatedPairs);
    const selectedCount = updatedPairs.filter(p => p.isSelected).length;
    addLog(`筛选完成，已选中${selectedCount}个转账对`, 'info');
  };

  // 删除选中的转账对
  const handleDeleteSelected = () => {
    const selectedCount = transferPairs.filter(p => p.isSelected).length;
    if (selectedCount === 0) {
      message.warning('没有选中的转账对');
      return;
    }

    setTransferPairs(prev => prev.filter(pair => !pair.isSelected));
    addLog(`已删除${selectedCount}个转账对`, 'info');
    message.success(`已删除${selectedCount}个转账对`);
  };

  // 导出选中的转账对
  const handleExportSelected = () => {
    const selectedPairs = transferPairs.filter(p => p.isSelected);
    if (selectedPairs.length === 0) {
      message.warning('没有选中的转账对');
      return;
    }

    const csvContent = [
      'Sender Address,Private Key,Receiver Address',
      ...selectedPairs.map(pair =>
        `${pair.senderAddress},${pair.privateKey},${pair.receiverAddress}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `多对多转账_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    saveAs(blob, fileName);

    addLog(`导出${selectedPairs.length}个转账对到CSV文件`, 'success');
    message.success('导出成功');
  };

  // 查询余额
  const handleQueryBalances = async () => {
    if (transferPairs.length === 0) {
      message.warning('没有转账对需要查询');
      return;
    }

    setLoading(true);
    addLog('开始查询余额...', 'info');

    try {
      const provider = new ethers.JsonRpcProvider(configState.network.rpcUrl);
      const updatedPairs = [...transferPairs];

      for (let i = 0; i < updatedPairs.length; i++) {
        const pair = updatedPairs[i];

        try {
          // 查询主币余额
          const nativeBalance = await provider.getBalance(pair.senderAddress);
          pair.nativeBalance = ethers.formatEther(nativeBalance);

          // 查询Token余额（如果选择了自定义代币）
          if (configState.selectedToken.address !== 'native') {
            const tokenContract = new ethers.Contract(
              configState.selectedToken.address,
              ERC20_ABI,
              provider
            );
            const tokenBalance = await tokenContract.balanceOf(pair.senderAddress);
            pair.tokenBalance = ethers.formatUnits(tokenBalance, configState.selectedToken.decimals);
          }

          addLog(`已更新 ${pair.senderAddress.slice(0, 6)}...${pair.senderAddress.slice(-4)} 余额`, 'info');
        } catch (error) {
          console.error(`查询 ${pair.senderAddress} 余额失败:`, error);
          addLog(`查询 ${pair.senderAddress.slice(0, 6)}...${pair.senderAddress.slice(-4)} 余额失败`, 'error');
        }
      }

      setTransferPairs(updatedPairs);
      addLog('余额查询完成', 'success');
      message.success('余额查询完成');
    } catch (error) {
      console.error('查询余额失败:', error);
      addLog('查询余额失败', 'error');
      message.error('查询余额失败');
    } finally {
      setLoading(false);
    }
  };

  // 核心转账功能
  const handleStartTransfer = async () => {
    const selectedPairs = transferPairs.filter(p => p.isSelected);

    // 前置检查
    if (selectedPairs.length === 0) {
      message.error('请至少选择一个转账对');
      return;
    }

    // 检查接收地址
    const invalidPairs = selectedPairs.filter(pair => !pair.receiverAddress || !isValidAddress(pair.receiverAddress));
    if (invalidPairs.length > 0) {
      message.error(`发现${invalidPairs.length}个转账对的接收地址无效`);
      return;
    }

    // 检查数量配置
    if (configState.amountMode === 'fixed' && !configState.amountSettings.fixed) {
      message.error('请输入固定转账数量');
      return;
    }

    setIsTransferring(true);
    addLog('开始多对多转账任务...', 'info');

    try {
      const provider = new ethers.JsonRpcProvider(configState.network.rpcUrl);

      for (const pair of selectedPairs) {
        try {
          updatePairStatus(pair.id, 'in-progress', '正在处理...');
          addLog(`开始处理转账对: ${pair.senderAddress.slice(0, 6)}...${pair.senderAddress.slice(-4)} -> ${pair.receiverAddress.slice(0, 6)}...${pair.receiverAddress.slice(-4)}`, 'info');

          const signer = new ethers.Wallet(pair.privateKey, provider);
          const feeData = await provider.getFeeData();

          // 计算转账数量
          let amountToSend: bigint;
          const isNativeToken = configState.selectedToken.address === 'native';
          const currentBalance = isNativeToken
            ? ethers.parseEther(pair.nativeBalance || '0')
            : ethers.parseUnits(pair.tokenBalance || '0', configState.selectedToken.decimals);

          switch (configState.amountMode) {
            case 'all':
              if (isNativeToken) {
                // 主币需要扣除Gas费用
                const gasLimit = BigInt(configState.gasLimit || '21000');
                const gasPrice = configState.gasPrice
                  ? ethers.parseUnits(configState.gasPrice, 'gwei')
                  : (feeData.gasPrice || BigInt(0));
                const gasCost = gasLimit * gasPrice;
                amountToSend = currentBalance - gasCost;

                if (amountToSend <= BigInt(0)) {
                  throw new Error('余额不足支付Gas费用');
                }
              } else {
                amountToSend = currentBalance;
              }
              break;

            case 'fixed':
              amountToSend = isNativeToken
                ? ethers.parseEther(configState.amountSettings.fixed)
                : ethers.parseUnits(configState.amountSettings.fixed, configState.selectedToken.decimals);
              break;

            case 'percentage':
              const percentage = BigInt(configState.amountSettings.percentage);
              amountToSend = (currentBalance * percentage) / BigInt(100);

              if (isNativeToken) {
                // 主币需要确保扣除Gas费用后还有余额
                const gasLimit = BigInt(configState.gasLimit || '21000');
                const gasPrice = configState.gasPrice
                  ? ethers.parseUnits(configState.gasPrice, 'gwei')
                  : (feeData.gasPrice || BigInt(0));
                const gasCost = gasLimit * gasPrice;

                if (currentBalance - amountToSend < gasCost) {
                  amountToSend = currentBalance - gasCost;
                  if (amountToSend <= BigInt(0)) {
                    throw new Error('余额不足支付Gas费用');
                  }
                }
              }
              break;

            default:
              throw new Error('无效的数量模式');
          }

          if (amountToSend <= BigInt(0)) {
            throw new Error('转账数量必须大于0');
          }

          // 构建交易
          let tx: any = {
            to: pair.receiverAddress,
            gasLimit: configState.gasLimit || (isNativeToken ? '21000' : '65000'),
          };

          // 设置Gas价格
          if (configState.is1559) {
            tx.maxFeePerGas = feeData.maxFeePerGas;
            tx.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
          } else {
            tx.gasPrice = configState.gasPrice
              ? ethers.parseUnits(configState.gasPrice, 'gwei')
              : feeData.gasPrice;
          }

          if (isNativeToken) {
            // 转账主币
            tx.value = amountToSend;
          } else {
            // 转账Token
            tx.to = configState.selectedToken.address;
            tx.value = BigInt(0);

            const tokenContract = new ethers.Contract(configState.selectedToken.address, ERC20_ABI, signer);
            tx.data = tokenContract.interface.encodeFunctionData('transfer', [pair.receiverAddress, amountToSend]);
          }

          // 发送交易
          const txResponse = await signer.sendTransaction(tx);
          const amountFormatted = isNativeToken
            ? ethers.formatEther(amountToSend)
            : ethers.formatUnits(amountToSend, configState.selectedToken.decimals);

          addLog(`转账成功: ${amountFormatted} ${configState.selectedToken.symbol}, TxHash: ${txResponse.hash}`, 'success');
          updatePairStatus(pair.id, 'success', `交易Hash: ${txResponse.hash}`);

          // 等待交易确认
          await txResponse.wait();
          addLog(`交易已确认: ${txResponse.hash}`, 'success');

        } catch (error: any) {
          console.error(`转账失败 ${pair.senderAddress}:`, error);
          const errorMessage = error.message || error.toString();
          addLog(`转账失败 ${pair.senderAddress.slice(0, 6)}...${pair.senderAddress.slice(-4)}: ${errorMessage}`, 'error');
          updatePairStatus(pair.id, 'error', `错误: ${errorMessage}`);
        }

        // 随机延迟
        const delay = Math.random() * (configState.delayMax - configState.delayMin) + configState.delayMin;
        await new Promise(resolve => setTimeout(resolve, delay * 1000));
      }

      addLog('所有转账任务已完成', 'success');
      message.success('所有转账任务已完成');

      // 自动刷新余额
      setTimeout(() => {
        handleQueryBalances();
      }, 2000);

    } catch (error: any) {
      console.error('转账任务失败:', error);
      addLog(`转账任务失败: ${error.message || error}`, 'error');
      message.error('转账任务失败');
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        多对多转账工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsSenderModalVisible(true)}
            style={buttonStyle}
          >
            导入发送地址
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={() => setIsReceiverModalVisible(true)}
            style={buttonStyle}
          >
            导入接收地址
          </Button>
          <Button
            icon={<FilterOutlined />}
            onClick={() => handleFilterByBalance('zero')}
            style={buttonStyle}
          >
            余额为0
          </Button>
          <Button
            icon={<FilterOutlined />}
            onClick={() => handleFilterByBalance('positive')}
            style={buttonStyle}
          >
            余额大于0
          </Button>
          <Button
            onClick={() => handleFilterByBalance('invert')}
            style={buttonStyle}
          >
            反选
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            style={buttonStyle}
          >
            删除选中
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportSelected}
            style={buttonStyle}
          >
            导出选中
          </Button>
        </Space>
      </Card>

      {/* 转账对表格 */}
      {transferPairs.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              转账对列表 ({transferPairs.length}个)
            </h3>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setTransferPairs([]);
                addLog('已清空所有转账对', 'info');
              }}
              style={buttonStyle}
            >
              清空全部
            </Button>
          </div>

          <Table
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '发送地址',
                dataIndex: 'senderAddress',
                width: 200,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '发送地址')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: '接收地址',
                dataIndex: 'receiverAddress',
                width: 250,
                render: (text: string, record: TransferPair) => (
                  <Input
                    value={text}
                    placeholder="输入接收地址"
                    style={{ ...componentStyle, fontSize: '12px' }}
                    onBlur={(e) => {
                      const newValue = e.target.value.trim();
                      setTransferPairs(prev => prev.map(pair =>
                        pair.id === record.id ? { ...pair, receiverAddress: newValue } : pair
                      ));
                    }}
                  />
                ),
              },
              {
                title: `${configState.selectedToken.symbol}余额`,
                dataIndex: configState.selectedToken.address === 'native' ? 'nativeBalance' : 'tokenBalance',
                width: 120,
                render: (text: string) => text || '-',
              },
              {
                title: '状态',
                dataIndex: 'status',
                width: 100,
                render: (status: string, record: TransferPair) => {
                  const statusConfig = {
                    idle: { color: 'default', text: '待处理' },
                    'in-progress': { color: 'processing', text: '处理中' },
                    success: { color: 'success', text: '成功' },
                    error: { color: 'error', text: '失败' }
                  };
                  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.idle;
                  return (
                    <Tooltip title={record.statusMessage}>
                      <Tag color={config.color}>{config.text}</Tag>
                    </Tooltip>
                  );
                },
              },
              {
                title: '操作',
                width: 80,
                render: (_, record: TransferPair) => (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setTransferPairs(prev => prev.filter(pair => pair.id !== record.id));
                      addLog(`已删除转账对: ${record.senderAddress.slice(0, 6)}...${record.senderAddress.slice(-4)}`, 'info');
                    }}
                  />
                ),
              },
            ]}
            dataSource={transferPairs}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: transferPairs.filter(p => p.isSelected).map(p => p.id),
              onChange: (selectedRowKeys) => {
                setTransferPairs(prev => prev.map(pair => ({
                  ...pair,
                  isSelected: selectedRowKeys.includes(pair.id)
                })));
              },
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个转账对`,
            }}
            scroll={{ x: 1000 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {transferPairs.filter(p => p.isSelected).length} 个</strong>
              </Col>
              <Col span={8}>
                <strong>总{configState.selectedToken.symbol}余额: {
                  transferPairs
                    .filter(p => p.isSelected)
                    .reduce((sum, p) => {
                      const balance = configState.selectedToken.address === 'native'
                        ? p.nativeBalance
                        : p.tokenBalance;
                      return sum + (parseFloat(balance || '0'));
                    }, 0)
                    .toFixed(6)
                }</strong>
              </Col>
              <Col span={8}>
                <strong>待处理: {transferPairs.filter(p => p.isSelected && p.status === 'idle').length} 个</strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 底部配置面板 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          转账配置
        </h3>

        {/* 网络配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h4>
              <Select
                value={configState.network.name}
                onChange={(value) => {
                  const network = NETWORK_CONFIGS.find(n => n.name === value);
                  if (network) {
                    setConfigState(prev => ({ ...prev, network }));
                  }
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
              >
                {NETWORK_CONFIGS.map(network => (
                  <Select.Option key={network.name} value={network.name}>
                    {network.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                RPC节点
                {liveGasPrice && (
                  <Tag color="blue" style={{ marginLeft: '8px' }}>
                    Gas: {liveGasPrice} Gwei
                  </Tag>
                )}
              </h4>
              <Input
                value={configState.network.rpcUrl}
                onChange={(e) => setConfigState(prev => ({
                  ...prev,
                  network: { ...prev.network, rpcUrl: e.target.value }
                }))}
                style={componentStyle}
                placeholder="RPC URL"
              />
            </div>
          </Col>
        </Row>

        {/* 代币选择 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={16}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择代币
              </h4>
              <Select
                value={configState.selectedToken.address}
                onChange={(value) => {
                  if (value === 'native') {
                    setConfigState(prev => ({
                      ...prev,
                      selectedToken: { ...NATIVE_TOKEN, symbol: prev.network.symbol }
                    }));
                  } else {
                    const token = customTokens.find(t => t.address === value);
                    if (token) {
                      setConfigState(prev => ({ ...prev, selectedToken: token }));
                    }
                  }
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择代币"
              >
                <Select.Option value="native">
                  {configState.network.symbol} (原生代币)
                </Select.Option>
                {customTokens.map(token => (
                  <Select.Option key={token.address} value={token.address}>
                    {token.symbol} - {token.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ paddingTop: '24px' }}>
              <Space>
                <Button
                  onClick={() => setIsTokenModalVisible(true)}
                  style={buttonStyle}
                  icon={<PlusOutlined />}
                >
                  添加代币
                </Button>
                <Button
                  onClick={handleQueryBalances}
                  loading={loading}
                  style={buttonStyle}
                  icon={<ReloadOutlined />}
                >
                  查询余额
                </Button>
              </Space>
            </div>
          </Col>
        </Row>

        {/* 数量配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                转账数量模式
              </h4>
              <Radio.Group
                value={configState.amountMode}
                onChange={(e) => setConfigState(prev => ({ ...prev, amountMode: e.target.value }))}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="all">全部数量 (扣除Gas费用)</Radio>
                  <Radio value="fixed">
                    固定数量:
                    <Input
                      value={configState.amountSettings.fixed}
                      onChange={(e) => setConfigState(prev => ({
                        ...prev,
                        amountSettings: { ...prev.amountSettings, fixed: e.target.value }
                      }))}
                      style={{ ...componentStyle, width: '200px', marginLeft: '8px' }}
                      placeholder={`输入${configState.selectedToken.symbol}数量`}
                      disabled={configState.amountMode !== 'fixed'}
                    />
                  </Radio>
                  <Radio value="percentage">
                    按百分比:
                    <InputNumber
                      value={configState.amountSettings.percentage}
                      onChange={(value) => setConfigState(prev => ({
                        ...prev,
                        amountSettings: { ...prev.amountSettings, percentage: value || 100 }
                      }))}
                      min={1}
                      max={100}
                      style={{ ...componentStyle, width: '100px', marginLeft: '8px' }}
                      disabled={configState.amountMode !== 'percentage'}
                      formatter={value => `${value}%`}
                      parser={value => Number(value!.replace('%', ''))}
                    />
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
          </Col>
        </Row>

        {/* Gas和延迟配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gas Limit
              </h4>
              <Input
                value={configState.gasLimit}
                onChange={(e) => setConfigState(prev => ({ ...prev, gasLimit: e.target.value }))}
                style={componentStyle}
                placeholder="21000"
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gas Price (Gwei)
              </h4>
              <Input
                value={configState.gasPrice}
                onChange={(e) => setConfigState(prev => ({ ...prev, gasPrice: e.target.value }))}
                style={componentStyle}
                placeholder="自动获取"
                disabled={configState.is1559}
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ paddingTop: '24px' }}>
              <Radio.Group
                value={configState.is1559}
                onChange={(e) => setConfigState(prev => ({ ...prev, is1559: e.target.value }))}
              >
                <Radio value={true}>EIP-1559</Radio>
                <Radio value={false}>传统Gas</Radio>
              </Radio.Group>
            </div>
          </Col>
        </Row>

        {/* 延迟配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                随机延迟范围 (秒)
              </h4>
              <Space>
                <InputNumber
                  value={configState.delayMin}
                  onChange={(value) => setConfigState(prev => ({ ...prev, delayMin: value || 1 }))}
                  min={0.1}
                  max={60}
                  step={0.1}
                  style={componentStyle}
                  placeholder="最小"
                />
                <span>-</span>
                <InputNumber
                  value={configState.delayMax}
                  onChange={(value) => setConfigState(prev => ({ ...prev, delayMax: value || 3 }))}
                  min={0.1}
                  max={60}
                  step={0.1}
                  style={componentStyle}
                  placeholder="最大"
                />
              </Space>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div style={{ paddingTop: '24px', textAlign: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<SendOutlined />}
                onClick={handleStartTransfer}
                disabled={transferPairs.filter(p => p.isSelected).length === 0 || isTransferring}
                loading={isTransferring}
                style={{
                  ...primaryButtonStyle,
                  minWidth: '160px',
                  height: '44px',
                  fontSize: '16px'
                }}
              >
                开始转账
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 日志区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          操作日志
        </h3>
        <div
          ref={logsRef}
          style={{
            height: '200px',
            overflow: 'auto',
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: '#999', textAlign: 'center', paddingTop: '60px' }}>
              暂无日志
            </div>
          ) : (
            logs.map((log, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                {log}
              </div>
            ))
          )}
        </div>
        <div style={{ marginTop: '8px', textAlign: 'right' }}>
          <Button
            size="small"
            onClick={() => setLogs([])}
            style={buttonStyle}
          >
            清空日志
          </Button>
        </div>
      </Card>

      {/* 导入发送地址模态框 */}
      <Modal
        title="导入发送地址私钥"
        open={isSenderModalVisible}
        onOk={handleImportSenders}
        onCancel={() => {
          setIsSenderModalVisible(false);
          setSendersInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="安全提醒"
          description="私钥仅在本地浏览器处理，不会上传或存储到服务器。页面关闭后私钥信息将从内存中清除。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="私钥列表 (每行一个私钥)"
            required
          >
            <Input.TextArea
              value={sendersInput}
              onChange={(e) => setSendersInput(e.target.value)}
              placeholder="请粘贴私钥，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入接收地址模态框 */}
      <Modal
        title="导入接收地址"
        open={isReceiverModalVisible}
        onOk={handleImportReceivers}
        onCancel={() => {
          setIsReceiverModalVisible(false);
          setReceiversInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Form layout="vertical">
          <Form.Item
            label="接收地址列表 (每行一个地址)"
            required
          >
            <Input.TextArea
              value={receiversInput}
              onChange={(e) => setReceiversInput(e.target.value)}
              placeholder="请粘贴接收地址，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加代币模态框 */}
      <Modal
        title="添加自定义代币"
        open={isTokenModalVisible}
        onOk={handleAddToken}
        onCancel={() => {
          setIsTokenModalVisible(false);
          setTokenAddressInput('');
        }}
        width={500}
        okText="添加代币"
        cancelText="取消"
      >
        <Form layout="vertical">
          <Form.Item
            label="代币合约地址"
            required
          >
            <Input
              value={tokenAddressInput}
              onChange={(e) => setTokenAddressInput(e.target.value)}
              placeholder="输入ERC20代币合约地址"
              style={componentStyle}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 安全提示 */}
      <Card size="small" style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        marginTop: '20px'
      }}>
        <div style={{ color: '#ff4d4f' }}>
          <strong>⚠️ 安全提醒：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>请妥善保管私钥，任何人获得私钥都可以控制您的钱包</li>
            <li>建议在安全的离线环境中使用此功能</li>
            <li>转账前请确认所有地址正确，交易一旦发送无法撤销</li>
            <li>建议先用小额测试，确认无误后再进行大额转账</li>
            <li>私钥仅在本地浏览器处理，不会上传到服务器</li>
            <li>请合理设置Gas费用和延迟时间，避免交易失败</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default MultiTransfer;
